import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useTheme } from '../contexts/ThemeContext';
import { getAmbulances, getTrackingSummary, getGpsDevices, getAllFuelRodData, Ambulance, GpsDevice } from '../services/api';
import { COLORS } from '../constants/booking';
import { formatDateDDMMYYYY, getMaxDateToday } from '../utils/dateUtils';
import { cumulativeDistancesKm, extractCoords } from '../utils/distance';
import { formatAmbulanceStatus } from './tracking/components/live-tracking/liveTrackingUtils';

interface FuelReading {
  fuelLevel: number;
  recordedAt: string;
}

interface FuelTimelineEvent {
  type: 'THEFT' | 'REFILL';
  percentChange: number;
  fuelChange: number;
  fuelLevel: number;
  timestamp: string;
}

interface Summary {
  distance: number;
  engineHours: number; // seconds
  maxSpeed: number;
  averageSpeed: number;
  trips?: number;
  runningTimeSeconds?: number;
  fuelRefilledLitres?: number;
  fuelConsumedLitres?: number;
}

interface LocationHistoryEntry {
  date: string;
  speed?: number;
  odometer?: number;
  latitude?: number;
  longitude?: number;
  location?: number[]; // [longitude, latitude]
  vehicleInfo?: { id?: string; location?: number[]; odometer?: number; speed?: number };
}

// Distance helpers imported from ../utils/distance

interface FuelData {
  _id: string;
  deviceInfo: {
    imei: string;
  };
  date: string;
  fuelReadings: FuelReading[];
  fuelTimeline: FuelTimelineEvent[];
  locationHistory?: LocationHistoryEntry[];
  summary?: Summary;
}

const formatSelectedDateStr = (date: string): string => {
  const selectedDateObj = new Date(date);
  const year = selectedDateObj.getFullYear();
  const month = String(selectedDateObj.getMonth() + 1).padStart(2, '0');
  const day = String(selectedDateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const AMBULANCE_STATUS_COLORS: Record<string, string> = {
  available: '#10b981',
  on_duty: '#ef4444',
  off_duty: '#f59e0b',
  maintenance: '#64748b',
};

const getAmbulanceStatusBadgeStyle = (status: string | undefined, isDark: boolean) => {
  const color = AMBULANCE_STATUS_COLORS[(status || '').toLowerCase()] || '#64748b';
  return {
    color,
    backgroundColor: isDark ? `${color}22` : `${color}14`,
    border: `1px solid ${color}`,
  };
};

const hasFuelReadingsForDate = (summaryData: any, selectedDate: string): boolean => {
  if (!summaryData) return false;

  const selectedDateStart = new Date(selectedDate);
  selectedDateStart.setHours(0, 0, 0, 0);
  const selectedDateEnd = new Date(selectedDate);
  selectedDateEnd.setHours(23, 59, 59, 999);

  return (summaryData.fuelReadings || []).some((reading: any) => {
    const readingDate = new Date(reading.recordedAt);
    return readingDate >= selectedDateStart && readingDate <= selectedDateEnd;
  });
};

const getFuelCardBackground = (
  hasFuelReadings: boolean,
  fuelReadingsKnown: boolean,
  isDark: boolean
): string => {
  if (!fuelReadingsKnown) {
    return isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND;
  }
  if (hasFuelReadings) {
    return isDark ? 'rgba(16, 185, 129, 0.08)' : '#f7fef9';
  }
  return isDark ? 'rgba(239, 68, 68, 0.08)' : '#fffafa';
};

const getFuelCardHoverBackground = (
  hasFuelReadings: boolean,
  fuelReadingsKnown: boolean,
  isDark: boolean
): string => {
  if (!fuelReadingsKnown) {
    return isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)';
  }
  if (hasFuelReadings) {
    return isDark ? 'rgba(16, 185, 129, 0.15)' : '#f0fdf4';
  }
  return isDark ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2';
};

const hasFuelConfiguration = (device: GpsDevice | null | undefined): boolean => {
  if (!device?.spec?.fuelDevice) return false;
  const fuelDevice = device.spec.fuelDevice;
  const hasEnabled = fuelDevice.enabled === true;
  const hasTypePorts = Array.isArray(fuelDevice.typePorts) && fuelDevice.typePorts.length > 0;
  const hasCalibration = Array.isArray(fuelDevice.calibration) && fuelDevice.calibration.length > 0;
  const hasThreshold = typeof fuelDevice.threshold === 'number';
  return hasEnabled || hasTypePorts || hasCalibration || hasThreshold;
};

const getFuelConfiguredAmbulanceIds = (
  ambulancesList: Ambulance[],
  gpsDevices: GpsDevice[]
): Set<string> => {
  const fuelConfiguredAmbulanceIds = new Set<string>();

  gpsDevices.forEach((device) => {
    if (!hasFuelConfiguration(device)) return;

    const ambulanceIdFromDevice = device.spec.ambulance?.id;
    if (ambulanceIdFromDevice) {
      fuelConfiguredAmbulanceIds.add(ambulanceIdFromDevice);
    }
  });

  if (fuelConfiguredAmbulanceIds.size === 0 && gpsDevices.length > 0) {
    const fuelConfiguredGpsIds = new Set(
      gpsDevices.filter(hasFuelConfiguration).map((d) => d._id)
    );
    ambulancesList.forEach((amb) => {
      const gpsId = amb.spec?.gpsDevice?.gpsDeviceId;
      if (gpsId && fuelConfiguredGpsIds.has(gpsId)) {
        fuelConfiguredAmbulanceIds.add(amb._id);
      }
    });
  }

  return fuelConfiguredAmbulanceIds;
};

const Fuel: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [ambulances, setAmbulances] = useState<Ambulance[]>([]);
  const [fuelReadingsAvailability, setFuelReadingsAvailability] = useState<Record<string, boolean>>({});
  const [selectedAmbulance, setSelectedAmbulance] = useState<Ambulance | null>(null);
  const [fuelData, setFuelData] = useState<FuelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [fuelLoading, setFuelLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [hoveredDistancePoint, setHoveredDistancePoint] = useState<number | null>(null);
  const [distanceTooltipPosition, setDistanceTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [hoveredTimelineEvent, setHoveredTimelineEvent] = useState<number | null>(null);
  const [timelineTooltipPosition, setTimelineTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [hoveredTimelineItem, setHoveredTimelineItem] = useState<number | null>(null);
  const [timelineItemTooltipPosition, setTimelineItemTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [ambulanceSearchOpen, setAmbulanceSearchOpen] = useState(false);
  const [ambulanceSearchTerm, setAmbulanceSearchTerm] = useState('');
  const [isChartFullscreen, setIsChartFullscreen] = useState(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Fetch ambulances with fuel configuration enabled
  useEffect(() => {
    const fetchAmbulances = async () => {
      try {
        setLoading(true);
        const [ambulancesResponse, gpsDevicesResponse] = await Promise.all([
          getAmbulances(1, 1000),
          getGpsDevices(1, 1000),
        ]);

        const ambulancesList: Ambulance[] = ambulancesResponse.data.data || [];
        const gpsDevices: GpsDevice[] = gpsDevicesResponse.data?.data || [];
        const fuelConfiguredAmbulanceIds = getFuelConfiguredAmbulanceIds(ambulancesList, gpsDevices);

        const fuelConfiguredAmbulances =
          fuelConfiguredAmbulanceIds.size > 0
            ? ambulancesList.filter((amb) => fuelConfiguredAmbulanceIds.has(amb._id))
            : [];

        setAmbulances(fuelConfiguredAmbulances);

        if (fuelConfiguredAmbulances.length > 0 && !selectedAmbulance) {
          setSelectedAmbulance(fuelConfiguredAmbulances[0]);
        }
      } catch (error: any) {
        console.error('Failed to fetch ambulances or GPS devices for fuel page:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAmbulances();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchFuelReadingsAvailability = async () => {
      const availabilityMap: Record<string, boolean> = {};

      ambulances.forEach((amb) => {
        availabilityMap[amb._id] = false;
      });

      if (ambulances.length === 0) {
        if (!cancelled) setFuelReadingsAvailability(availabilityMap);
        return;
      }

      try {
        const dateStr = formatSelectedDateStr(selectedDate);
        const response = await getAllFuelRodData(dateStr);
        const items = response.data.data?.items || [];

        items.forEach((item) => {
          availabilityMap[item.ambulanceId] = hasFuelReadingsForDate(
            { fuelReadings: item.fuelReadings },
            selectedDate
          );
        });
      } catch (error) {
        console.error('Failed to fetch fuel readings availability:', error);
      }

      if (!cancelled) {
        setFuelReadingsAvailability(availabilityMap);
      }
    };

    fetchFuelReadingsAvailability();
    return () => {
      cancelled = true;
    };
  }, [ambulances, selectedDate]);

  // Fetch fuel data when ambulance or date changes
  useEffect(() => {
    const fetchFuelData = async () => {
      if (!selectedAmbulance?.spec?.gpsDevice?.deviceId) {
        setFuelData(null);
        return;
      }

      try {
        setFuelLoading(true);
        const imei = selectedAmbulance.spec.gpsDevice.deviceId;
        const dateStr = formatSelectedDateStr(selectedDate);
        
        // Use the same date for both startDate and endDate
        const startDateStr = dateStr;
        const endDateStr = dateStr;

        // Call the summary API
        const response = await getTrackingSummary(imei, undefined, startDateStr, endDateStr);
        const summaryData = response.data.data;
        
        if (summaryData && (summaryData.fuelReadings || summaryData.fuelTimeline || (summaryData.locationHistory && summaryData.locationHistory.length > 0))) {
          // Filter and sort fuelReadings by the selected date
          const selectedDateStart = new Date(selectedDate);
          selectedDateStart.setHours(0, 0, 0, 0);
          const selectedDateEnd = new Date(selectedDate);
          selectedDateEnd.setHours(23, 59, 59, 999);
          
          let filteredReadings = (summaryData.fuelReadings || []).filter((reading: any) => {
            const readingDate = new Date(reading.recordedAt);
            return readingDate >= selectedDateStart && readingDate <= selectedDateEnd;
          });
          
          // Sort readings by time (ascending)
          filteredReadings.sort((a: any, b: any) => {
            return new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime();
          });
          
          // Filter and sort fuelTimeline by the selected date
          let filteredTimeline = (summaryData.fuelTimeline || []).filter((event: any) => {
            const eventDate = new Date(event.timestamp);
            return eventDate >= selectedDateStart && eventDate <= selectedDateEnd;
          });
          
          // Sort timeline events by time (ascending)
          filteredTimeline.sort((a: any, b: any) => {
            return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
          });
          
          // Filter locationHistory by selected date (structure: { date, vehicleInfo: { speed } })
          const rawLocationHistory = summaryData.locationHistory || [];
          const filteredLocationHistory = rawLocationHistory.filter((entry: any) => {
            const entryDate = new Date(entry.date);
            return entryDate >= selectedDateStart && entryDate <= selectedDateEnd;
          });
          filteredLocationHistory.sort((a: any, b: any) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
          );

          // Map the summary response to the FuelData interface
          const mappedFuelData: FuelData = {
            _id: summaryData.imeiNo || imei,
            deviceInfo: {
              imei: summaryData.imeiNo || imei,
            },
            date: summaryData.date || selectedDate,
            fuelReadings: filteredReadings,
            fuelTimeline: filteredTimeline,
            locationHistory: filteredLocationHistory,
            summary: summaryData.summary || undefined,
          };
          setFuelData(mappedFuelData);
          setFuelReadingsAvailability((prev) => ({
            ...prev,
            [selectedAmbulance._id]: filteredReadings.length > 0,
          }));
        } else {
          setFuelData(null);
          setFuelReadingsAvailability((prev) => ({
            ...prev,
            [selectedAmbulance._id]: false,
          }));
        }
      } catch (error: any) {
        console.error('Failed to fetch fuel data:', error);
        setFuelData(null);
        if (selectedAmbulance) {
          setFuelReadingsAvailability((prev) => ({
            ...prev,
            [selectedAmbulance._id]: false,
          }));
        }
      } finally {
        setFuelLoading(false);
      }
    };

    fetchFuelData();
  }, [selectedAmbulance, selectedDate]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsChartFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Handle fullscreen toggle
  const handleChartFullscreen = async () => {
    if (!chartContainerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await chartContainerRef.current.requestFullscreen();
        setIsChartFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsChartFullscreen(false);
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
    }
  };

  // Render fuel readings chart (fuel + speed)
  const renderFuelChart = () => {
    const readings = (fuelData?.fuelReadings || []).length > 0
      ? [...(fuelData!.fuelReadings)].sort((a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime())
      : [];
    const timelineEvents = fuelData?.fuelTimeline || [];
    const locationHistory = [...(fuelData?.locationHistory || [])].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    const hasReadings = readings.length > 0;
    const hasLocationCoords = locationHistory.length >= 2 && locationHistory.some(e => extractCoords(e) !== null);
    const hasData = hasReadings || hasLocationCoords;

    if (!hasData) {
      return (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
        }}>
          No fuel readings or distance data available for selected date
        </div>
      );
    }

    const paddingLeft = 62; // Enough space so "14.96L" / "20.76L" are not cut off
    const paddingRight = 95; // Right: speed + distance axes
    const paddingTop = 50;
    const paddingBottom = 50;
    const svgWidth = isChartFullscreen ? 1400 : 800;
    const height = isChartFullscreen ? 600 : 300;
    const chartWidth = svgWidth - paddingLeft - paddingRight;
    const chartHeight = height - paddingTop - paddingBottom;

    // Time range: include fuel, timeline, and distance
    const allTimestamps = [
      ...readings.map(r => new Date(r.recordedAt).getTime()),
      ...timelineEvents.map(e => new Date(e.timestamp).getTime()),
      ...locationHistory.map(e => new Date(e.date).getTime()),
    ];
    const firstTime = Math.min(...allTimestamps);
    const lastTime = Math.max(...allTimestamps);
    const timeRange = lastTime - firstTime || 1;

    // Fuel scale (left axis)
    const allFuelLevels = [
      ...readings.map(r => r.fuelLevel),
      ...timelineEvents.map(e => e.fuelLevel),
    ];
    const rawMinFuel = allFuelLevels.length > 0 ? Math.min(...allFuelLevels) : 0;
    const rawMaxFuel = allFuelLevels.length > 0 ? Math.max(...allFuelLevels) : 20;
    const fuelRangePadding = rawMaxFuel === rawMinFuel ? 5 : (rawMaxFuel - rawMinFuel) * 0.1;
    const minFuel = Math.max(0, rawMinFuel - fuelRangePadding);
    const maxFuel = rawMaxFuel + fuelRangePadding;
    const fuelRange = maxFuel - minFuel || 1;

    // Cumulative GPS-based distance (Haversine) — same method as the vehicle report
    const distancesKm = cumulativeDistancesKm(locationHistory);
    const maxDistanceKm = distancesKm.length > 0 ? Math.max(...distancesKm) : 1;
    const distanceRange = maxDistanceKm > 0 ? maxDistanceKm * 1.05 : 1;
    const hasDistance = distancesKm.length >= 2 && maxDistanceKm > 0;

    // Fuel points
    const points = readings.map((reading, index) => {
      const readingTime = new Date(reading.recordedAt).getTime();
      const x = paddingLeft + ((readingTime - firstTime) / timeRange) * chartWidth;
      const y = paddingTop + chartHeight - ((reading.fuelLevel - minFuel) / fuelRange) * chartHeight;
      return { x, y, reading, index };
    });

    // Map timeline events to chart coordinates
    const timelinePoints: Array<{ x: number; y: number; event: FuelTimelineEvent; index: number }> = timelineEvents.map((event, index) => {
      const eventTime = new Date(event.timestamp).getTime();
      const x = paddingLeft + ((eventTime - firstTime) / timeRange) * chartWidth;
      const y = paddingTop + chartHeight - ((event.fuelLevel - minFuel) / fuelRange) * chartHeight;
      return { x, y, event, index };
    });

    // Distance points (right Y-axis, same time base; distance in km)
    const distancePoints = locationHistory.map((entry, index) => {
      const t = new Date(entry.date).getTime();
      const distKm = distancesKm[index] ?? 0;
      const x = paddingLeft + ((t - firstTime) / timeRange) * chartWidth;
      const y = paddingTop + chartHeight - (distKm / distanceRange) * chartHeight;
      return { x, y, distanceKm: distKm, date: entry.date, index };
    });

    // Fuel line path
    let pathData: string;
    if (points.length === 1) {
      pathData = `M ${paddingLeft} ${points[0].y} L ${svgWidth - paddingRight} ${points[0].y}`;
    } else if (points.length > 0) {
      pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    } else {
      pathData = '';
    }

    // Distance line path – downsample when many points
    const maxDistPathPoints = 120;
    const distancePathPointsSampled =
      distancePoints.length <= maxDistPathPoints
        ? distancePoints
        : (() => {
            const step = (distancePoints.length - 1) / (maxDistPathPoints - 1);
            const result: typeof distancePoints = [];
            for (let i = 0; i < maxDistPathPoints; i++) {
              const idx = i === maxDistPathPoints - 1 ? distancePoints.length - 1 : Math.round(i * step);
              result.push(distancePoints[idx]);
            }
            return result;
          })();
    let distancePathData: string;
    if (distancePathPointsSampled.length === 1) {
      distancePathData = `M ${paddingLeft} ${distancePathPointsSampled[0].y} L ${svgWidth - paddingRight} ${distancePathPointsSampled[0].y}`;
    } else if (distancePathPointsSampled.length > 0) {
      distancePathData = distancePathPointsSampled.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    } else {
      distancePathData = '';
    }

    // Handle mouse move for tooltip
    const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
      const svg = e.currentTarget;
      const rect = svg.getBoundingClientRect();
      const svgPoint = svg.createSVGPoint();
      svgPoint.x = e.clientX - rect.left;
      svgPoint.y = e.clientY - rect.top;

      // Scale to viewBox coordinates
      const viewBox = svg.viewBox.baseVal;
      const scaleX = viewBox.width / rect.width;
      const scaleY = viewBox.height / rect.height;
      const x = svgPoint.x * scaleX;
      const y = svgPoint.y * scaleY;

      // Check if hovering over timeline event first (they're more important)
      let closestTimelineEvent: { x: number; y: number; event: FuelTimelineEvent; index: number } | null = null;
      let minTimelineDistance = 20; // 20px threshold

      for (const point of timelinePoints) {
        const distance = Math.sqrt(Math.pow(x - point.x, 2) + Math.pow(y - point.y, 2));
        if (distance < minTimelineDistance) {
          minTimelineDistance = distance;
          closestTimelineEvent = point;
        }
      }

      if (closestTimelineEvent !== null) {
        setHoveredTimelineEvent(closestTimelineEvent.index);
        setTimelineTooltipPosition({ x: e.clientX, y: e.clientY });
        setHoveredPoint(null);
        setTooltipPosition(null);
        setHoveredDistancePoint(null);
        setDistanceTooltipPosition(null);
      } else {
        setHoveredTimelineEvent(null);
        setTimelineTooltipPosition(null);

        if (points.length > 0) {
          let closestPoint = points[0];
          let minDistance = Math.abs(x - closestPoint.x);
          points.forEach((point) => {
            const distance = Math.abs(x - point.x);
            if (distance < minDistance) {
              minDistance = distance;
              closestPoint = point;
            }
          });
          setHoveredPoint(closestPoint.index);
          setTooltipPosition({ x: e.clientX, y: e.clientY });
          setHoveredDistancePoint(null);
          setDistanceTooltipPosition(null);
        } else if (distancePoints.length > 0) {
          // No fuel points, but we have distance: hover based on distance points
          let closestDistPoint = distancePoints[0];
          let minDistance = Math.abs(x - closestDistPoint.x);
          distancePoints.forEach((point) => {
            const distance = Math.abs(x - point.x);
            if (distance < minDistance) {
              minDistance = distance;
              closestDistPoint = point;
            }
          });
          setHoveredPoint(null);
          setTooltipPosition(null);
          setHoveredDistancePoint(closestDistPoint.index);
          setDistanceTooltipPosition({ x: e.clientX, y: e.clientY });
        } else {
          setHoveredPoint(null);
          setTooltipPosition(null);
          setHoveredDistancePoint(null);
          setDistanceTooltipPosition(null);
        }
      }
    };

    const handleMouseLeave = () => {
      setHoveredPoint(null);
      setTooltipPosition(null);
      setHoveredDistancePoint(null);
      setDistanceTooltipPosition(null);
      setHoveredTimelineEvent(null);
      setTimelineTooltipPosition(null);
    };

    const hoveredPointData = hoveredPoint !== null ? points[hoveredPoint] : null;

    // When hovering fuel, highlight the distance point only if it's close in time
    const HOVER_HIGHLIGHT_TIME_MS = 2 * 60 * 1000; // 2 minutes – don't highlight if closest point is far away
    const highlightedDistanceIndexFromFuel: number | null =
      hoveredPointData && distancePoints.length > 0
        ? (() => {
            const hoverTime = new Date(hoveredPointData.reading.recordedAt).getTime();
            let closest = 0;
            let minDiff = Math.abs(new Date(distancePoints[0].date).getTime() - hoverTime);
            distancePoints.forEach((p, i) => {
              const diff = Math.abs(new Date(p.date).getTime() - hoverTime);
              if (diff < minDiff) {
                minDiff = diff;
                closest = i;
              }
            });
            return minDiff <= HOVER_HIGHLIGHT_TIME_MS ? closest : null;
          })()
        : null;

    const highlightedDistanceIndex: number | null =
      highlightedDistanceIndexFromFuel !== null ? highlightedDistanceIndexFromFuel : hoveredDistancePoint;

    const chartContent = (
      <div 
        ref={chartContainerRef}
        style={{ 
          padding: '20px', 
          height: isChartFullscreen ? '100vh' : '100%', 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <h3 style={{
              margin: 0,
              color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
            }}>
              Readings Chart
            </h3>
            {/* Legend */}
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {hasReadings && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY }}>
                  <span style={{ width: 20, height: 3, backgroundColor: '#3b82f6', borderRadius: 1 }} />
                  Fuel (L)
                </span>
              )}
              {hasDistance && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY }}>
                  <span style={{ width: 20, height: 3, backgroundColor: '#10b981', borderRadius: 1 }} />
                  Distance (km)
                </span>
              )}
            </div>
          </div>
          <button
            onClick={handleChartFullscreen}
            style={{
              background: 'none',
              border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
              borderRadius: '6px',
              padding: '6px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
              fontSize: '15px',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            title={isChartFullscreen ? 'Exit fullscreen' : 'View fullscreen'}
          >
            {isChartFullscreen ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
                </svg>
                Exit Fullscreen
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                </svg>
              </>
            )}
          </button>
        </div>
        <div style={{ width: '100%', position: 'relative', flex: 1, minHeight: 0 }}>
          <svg 
            viewBox={`0 0 ${svgWidth} ${height}`} 
            preserveAspectRatio="xMidYMid meet"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ 
              width: '100%', 
              height: isChartFullscreen ? '100%' : `${height}px`,
              minHeight: isChartFullscreen ? '500px' : `${height}px`,
              cursor: 'crosshair'
            }}
          >
            {/* Grid lines with labels - left (fuel), right (distance) */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
              const y = paddingTop + chartHeight - ratio * chartHeight;
              const fuelValue = minFuel + ratio * fuelRange;
              const distanceValue = ratio * distanceRange;
              const isMinMax = ratio === 0 || ratio === 1;
              return (
                <g key={ratio}>
                  <line
                    x1={paddingLeft}
                    y1={y}
                    x2={svgWidth - paddingRight}
                    y2={y}
                    stroke={isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}
                    strokeWidth={1}
                    strokeDasharray="4,4"
                  />
                  {!isMinMax && hasReadings && (
                    <text
                      x={paddingLeft - 14}
                      y={y + 4}
                      fill={isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY}
                      fontSize="12"
                      textAnchor="end"
                    >
                      {fuelValue.toFixed(2)}L
                    </text>
                  )}
                  {/* Right axis: distance */}
                  {!isMinMax && hasDistance && (
                    <text
                      x={svgWidth - paddingRight + 10}
                      y={y + 4}
                      fill="#10b981"
                      fontSize="12"
                      textAnchor="start"
                    >
                      {distanceValue.toFixed(2)} km
                    </text>
                  )}
                </g>
              );
            })}

            {/* Left Y-axis labels (fuel min/max) – enough margin so "L" is not cut off */}
            {hasReadings && (
              <>
                <text
                  x={paddingLeft - 14}
                  y={paddingTop + 4}
                  fill={isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY}
                  fontSize="13"
                  textAnchor="end"
                  style={{ fontWeight: '600' }}
                >
                  {maxFuel.toFixed(2)}L
                </text>
                <text
                  x={paddingLeft - 14}
                  y={height - paddingBottom + 4}
                  fill={isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY}
                  fontSize="13"
                  textAnchor="end"
                  style={{ fontWeight: '600' }}
                >
                  {minFuel.toFixed(2)}L
                </text>
              </>
            )}

            {/* Right Y-axis: distance */}
            {hasDistance && (
              <>
                <text
                  x={svgWidth - paddingRight + 10}
                  y={paddingTop}
                  fill="#10b981"
                  fontSize="13"
                  textAnchor="start"
                  style={{ fontWeight: '600' }}
                >
                  {distanceRange.toFixed(2)} km
                </text>
                <text
                  x={svgWidth - paddingRight + 10}
                  y={height - paddingBottom + 5}
                  fill="#10b981"
                  fontSize="13"
                  textAnchor="start"
                  style={{ fontWeight: '600' }}
                >
                  0 km
                </text>
              </>
            )}

            {/* Fuel level line */}
            {pathData && (
              <path
                d={pathData}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="2"
                style={{ transition: 'stroke-width 0.2s' }}
              />
            )}

            {/* Distance line (GPS Haversine, km) */}
            {distancePathData && (
              <path
                d={distancePathData}
                fill="none"
                stroke="#10b981"
                strokeWidth="2"
                style={{ transition: 'stroke-width 0.2s' }}
              />
            )}

            {/* Vertical line indicator on hover */}
            {hoveredPointData && (
              <g>
                <line
                  x1={hoveredPointData.x}
                  y1={paddingTop}
                  x2={hoveredPointData.x}
                  y2={height - paddingBottom}
                  stroke="#3b82f6"
                  strokeWidth="1.5"
                  strokeDasharray="4,4"
                  opacity="0.6"
                />
                <circle
                  cx={hoveredPointData.x}
                  cy={hoveredPointData.y}
                  r="6"
                  fill="#3b82f6"
                  stroke="white"
                  strokeWidth="2"
                  style={{ transition: 'r 0.2s' }}
                />
              </g>
            )}

            {/* Distance: highlight the point at hovered time (when hovering fuel) */}
            {highlightedDistanceIndex !== null && distancePoints[highlightedDistanceIndex] && (
              <g>
                <circle
                  cx={distancePoints[highlightedDistanceIndex].x}
                  cy={distancePoints[highlightedDistanceIndex].y}
                  r="8"
                  fill="transparent"
                  stroke="#10b981"
                  strokeWidth="2"
                  opacity={0.8}
                />
                <circle
                  cx={distancePoints[highlightedDistanceIndex].x}
                  cy={distancePoints[highlightedDistanceIndex].y}
                  r="6"
                  fill="#10b981"
                  stroke="white"
                  strokeWidth="2"
                  style={{ transition: 'r 0.2s' }}
                />
              </g>
            )}

            {/* Fuel: invisible hit areas for hover; only draw visible point when highlighted */}
            {points.map((point, index) => (
              <g key={index}>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="8"
                  fill="transparent"
                  style={{ cursor: 'pointer' }}
                />
                {hoveredPoint === index && (
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="6"
                    fill="#3b82f6"
                    stroke="white"
                    strokeWidth="2"
                    style={{ transition: 'r 0.2s', cursor: 'pointer' }}
                  />
                )}
              </g>
            ))}

            {/* Timeline event dots (THEFT and REFILL) - rendered last for higher z-index */}
            {timelinePoints.map((point, index) => {
              const isTheft = point.event.type === 'THEFT';
              const isHovered = hoveredTimelineEvent === index;
              return (
                <g key={`timeline-${index}`} style={{ zIndex: 10 }}>
                  {/* Invisible larger hit area */}
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="10"
                    fill="transparent"
                    style={{ cursor: 'pointer' }}
                  />
                  {/* Colored dot for timeline event */}
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r={isHovered ? 8 : 6}
                    fill={isTheft ? "#ef4444" : "#10b981"}
                    stroke="white"
                    strokeWidth={isHovered ? 3 : 2}
                    style={{ 
                      transition: 'r 0.2s, stroke-width 0.2s',
                      cursor: 'pointer',
                      filter: isHovered ? 'drop-shadow(0 0 4px rgba(0,0,0,0.3))' : 'none'
                    }}
                  />
                </g>
              );
            })}

            {/* X-axis time labels */}
            {hasData && (() => {
              const intervals = [0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875, 1];
              return intervals.map((ratio, idx) => {
                const timeAtRatio = firstTime + (timeRange * ratio);
                const x = paddingLeft + (ratio * chartWidth);
                const time = new Date(timeAtRatio).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                });
                return (
                  <text
                    key={idx}
                    x={x}
                    y={height - paddingBottom + 20}
                    fill={isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY}
                    fontSize="13"
                    textAnchor="middle"
                    style={{ fontWeight: '600' }}
                  >
                    {time}
                  </text>
                );
              });
            })()}
          </svg>

          {/* Summary Section */}
          {fuelData?.summary && (
            <div style={{
              marginTop: '5px',
              padding: '10px',
              backgroundColor: isDark ? 'rgba(31, 41, 55, 0.5)' : 'rgba(249, 250, 251, 0.8)',
              borderRadius: '8px',
              // border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
              flexShrink: 0
            }}>
              <h4 style={{
                margin: '0 0 16px 0',
                fontSize: '18px',
                fontWeight: '600',
                color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY
              }}>
                Summary
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
              }}>
                {/* Distance */}
                <div style={{
                  padding: '12px',
                  backgroundColor: isDark ? 'rgba(17, 24, 39, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                  borderRadius: '6px',
                  border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
                    marginBottom: '4px'
                  }}>
                    Distance
                  </div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY
                  }}>
                    {(() => {
                      const lh = fuelData.locationHistory || [];
                      const lhSorted = [...lh].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                      if (lhSorted.length >= 2) {
                        const cum = cumulativeDistancesKm(lhSorted);
                        return cum[cum.length - 1].toFixed(2);
                      }
                      return fuelData.summary.distance.toFixed(2);
                    })()} km
                  </div>
                </div>

                {/* Trips */}
                <div style={{
                  padding: '12px',
                  backgroundColor: isDark ? 'rgba(17, 24, 39, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                  borderRadius: '6px',
                  border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
                    marginBottom: '4px'
                  }}>
                    Trips
                  </div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY
                  }}>
                    {fuelData.summary.trips ?? 0}
                  </div>
                </div>

                {/* Running Time */}
                <div style={{
                  padding: '12px',
                  backgroundColor: isDark ? 'rgba(17, 24, 39, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                  borderRadius: '6px',
                  border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
                    marginBottom: '4px'
                  }}>
                    Running Time
                  </div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY
                  }}>
                    {(() => {
                      const totalSeconds = fuelData.summary.runningTimeSeconds ?? fuelData.summary.engineHours ?? 0;
                      const hours = Math.floor(totalSeconds / 3600);
                      const minutes = Math.floor((totalSeconds % 3600) / 60);
                      return `${hours}h ${minutes}m`;
                    })()}
                  </div>
                </div>

                {/* Engine Hours */}
                <div style={{
                  padding: '12px',
                  backgroundColor: isDark ? 'rgba(17, 24, 39, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                  borderRadius: '6px',
                  border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
                    marginBottom: '4px'
                  }}>
                    Engine Hours
                  </div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY
                  }}>
                    {(() => {
                      const totalSeconds = fuelData.summary.engineHours ?? 0;
                      const hours = Math.floor(totalSeconds / 3600);
                      const minutes = Math.floor((totalSeconds % 3600) / 60);
                      return `${hours}h ${minutes}m`;
                    })()}
                  </div>
                </div>

                {/* Fuel Refilled */}
                <div style={{
                  padding: '12px',
                  backgroundColor: isDark ? 'rgba(17, 24, 39, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                  borderRadius: '6px',
                  border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
                    marginBottom: '4px'
                  }}>
                    Fuel Refilled
                  </div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY
                  }}>
                    {fuelData.summary.fuelRefilledLitres != null
                      ? `${fuelData.summary.fuelRefilledLitres.toFixed(1)} L`
                      : '—'}
                  </div>
                </div>

                {/* Fuel Consumed */}
                <div style={{
                  padding: '12px',
                  backgroundColor: isDark ? 'rgba(17, 24, 39, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                  borderRadius: '6px',
                  border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
                    marginBottom: '4px'
                  }}>
                    Fuel Consumed
                  </div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY
                  }}>
                    {fuelData.summary.fuelConsumedLitres != null
                      ? `${fuelData.summary.fuelConsumedLitres.toFixed(1)} L`
                      : '—'}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Tooltip for timeline events */}
          {hoveredTimelineEvent !== null && timelineTooltipPosition && hoveredTimelineEvent >= 0 && hoveredTimelineEvent < timelinePoints.length && (() => {
            const timelinePoint = timelinePoints[hoveredTimelineEvent];
            const isTheft = timelinePoint.event.type === 'THEFT';
            return (
              <div
                style={{
                  position: 'fixed',
                  left: `${timelineTooltipPosition.x}px`,
                  top: `${timelineTooltipPosition.y - 10}px`,
                  transform: 'translate(-50%, -100%)',
                  backgroundColor: isDark ? '#1f2937' : '#ffffff',
                  border: `2px solid ${isTheft ? '#ef4444' : '#10b981'}`,
                  borderRadius: '6px',
                  padding: '12px 14px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)',
                  pointerEvents: 'none',
                  zIndex: 1001,
                  minWidth: '180px',
                }}
              >
                <div style={{ 
                  fontSize: '15px', 
                  fontWeight: '700',
                  color: isTheft ? '#ef4444' : '#10b981',
                  marginBottom: '6px'
                }}>
                  {isTheft ? '🔴 Fuel Theft' : '🟢 Fuel Refill'}
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                  marginBottom: '4px'
                }}>
                  Fuel Level: {timelinePoint.event.fuelLevel.toFixed(2)}L
                </div>
                <div style={{ 
                  fontSize: '13px', 
                  color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
                  marginBottom: '4px'
                }}>
                  Change: {timelinePoint.event.fuelChange > 0 ? '+' : ''}{timelinePoint.event.fuelChange.toFixed(2)}L ({timelinePoint.event.percentChange > 0 ? '+' : ''}{timelinePoint.event.percentChange.toFixed(2)}%)
                </div>
                <div style={{ 
                  fontSize: '13px', 
                  color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY
                }}>
                  {new Date(timelinePoint.event.timestamp).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </div>
              </div>
            );
          })()}

          {/* Tooltip for reading points */}
          {hoveredPointData && tooltipPosition && hoveredTimelineEvent === null && (
            <div
              style={{
                position: 'fixed',
                left: `${tooltipPosition.x}px`,
                top: `${tooltipPosition.y - 10}px`,
                transform: 'translate(-50%, -100%)',
                backgroundColor: isDark ? '#1f2937' : '#ffffff',
                border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
                borderRadius: '6px',
                padding: '10px 12px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                pointerEvents: 'none',
                zIndex: 1000,
                minWidth: '160px',
              }}
            >
              {hasReadings && (
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                  marginBottom: '4px',
                }}>
                  Fuel Level: {hoveredPointData.reading.fuelLevel.toFixed(2)}L
                </div>
              )}
              {hasDistance && (() => {
                const hoverTime = new Date(hoveredPointData.reading.recordedAt).getTime();
                const closest = distancePoints.reduce((prev, curr) =>
                  Math.abs(new Date(curr.date).getTime() - hoverTime) < Math.abs(new Date(prev.date).getTime() - hoverTime) ? curr : prev
                );
                return (
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#10b981',
                    marginBottom: '4px',
                  }}>
                    Distance: {closest.distanceKm.toFixed(3)} km
                  </div>
                );
              })()}
              <div style={{
                fontSize: '13px',
                color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
              }}>
                {new Date(hoveredPointData.reading.recordedAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </div>
            </div>
          )}
          
          {/* Tooltip for distance-only (when there are no fuel points) */}
          {!hoveredPointData && hoveredDistancePoint !== null && distancePoints[hoveredDistancePoint] && distanceTooltipPosition && hoveredTimelineEvent === null && (
            <div
              style={{
                position: 'fixed',
                left: `${distanceTooltipPosition.x}px`,
                top: `${distanceTooltipPosition.y - 10}px`,
                transform: 'translate(-50%, -100%)',
                backgroundColor: isDark ? '#1f2937' : '#ffffff',
                border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
                borderRadius: '6px',
                padding: '10px 12px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                pointerEvents: 'none',
                zIndex: 1000,
                minWidth: '140px',
              }}
            >
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#10b981',
                marginBottom: '4px',
              }}>
                Distance: {distancePoints[hoveredDistancePoint].distanceKm.toFixed(3)} km
              </div>
              <div style={{
                fontSize: '13px',
                color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
              }}>
                {new Date(distancePoints[hoveredDistancePoint].date).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );

    if (isChartFullscreen) {
      return (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND,
            zIndex: 9999,
          }}
        >
          {chartContent}
        </div>
      );
    }

    return chartContent;
  };

  // Render timeline
  const renderTimeline = () => {
    const hasTimeline = fuelData && fuelData.fuelTimeline && fuelData.fuelTimeline.length > 0;
    
    if (!hasTimeline) {
      return (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY 
        }}>
          No timeline events for selected date
        </div>
      );
    }

    // Sort timeline events by timestamp (oldest first)
    const sortedTimeline = [...fuelData.fuelTimeline].sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    return (
      <div style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', minHeight: 0 }}>
        <h3 style={{ 
          marginBottom: '24px', 
          fontSize: '20px',
          fontWeight: '600',
          color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
          flexShrink: 0
        }}>
          Timeline
        </h3>
        <div style={{ position: 'relative', paddingLeft: '50px', paddingTop: '8px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {/* Vertical dotted line */}
          <div
            style={{
              position: 'absolute',
              left: '18px',
              top: '20px',
              bottom: '8px',
              width: '2px',
              borderLeft: `2px dotted ${isDark ? '#4b5563' : '#d1d5db'}`,
            }}
          />
          
          {sortedTimeline.map((event, index) => {
            const isTheft = event.type === 'THEFT';
            const timestamp = new Date(event.timestamp);
            const timeStr = timestamp.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true 
            });
            
            // Format change with sign
            const changeStr = `${isTheft ? '-' : '+'}${Math.abs(event.fuelChange).toFixed(2)}L`;
            const percentStr = `${event.percentChange > 0 ? '+' : ''}${event.percentChange.toFixed(2)}%`;
            const isHovered = hoveredTimelineItem === index;
            
            return (
              <div
                key={index}
                onMouseMove={(e) => {
                  setHoveredTimelineItem(index);
                  setTimelineItemTooltipPosition({
                    x: e.clientX,
                    y: e.clientY
                  });
                }}
                onMouseLeave={() => {
                  setHoveredTimelineItem(null);
                  setTimelineItemTooltipPosition(null);
                }}
                style={{
                  position: 'relative',
                  marginBottom: index < sortedTimeline.length - 1 ? '28px' : '0',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  opacity: isHovered ? 0.9 : 1,
                }}
              >
                {/* Icon */}
                <div
                  style={{
                    position: 'absolute',
                    left: '-50px',
                    top: '10px',
                    width: '36px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND,
                    flexShrink: 0,
                    zIndex: 1,
                  }}
                >
                  <img
                    src={isTheft ? '/images/fuel_theft.png' : '/images/refuel.png'}
                    alt={isTheft ? 'Fuel Theft' : 'Fuel Refill'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </div>

                {/* Content */}
                <div style={{ flex: 1, paddingTop: '4px' }}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '10px',
                    lineHeight: '1.4',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ 
                      fontWeight: '600', 
                      fontSize: '17px',
                      color: isDark ? '#ffffff' : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                    }}>
                      {isTheft ? 'Fuel Theft' : 'Fuel Refill'}
                    </div>
                    <div style={{ 
                      fontSize: '15px',
                      color: isDark ? '#9ca3af' : '#6b7280',
                    }}>
                      {timeStr}
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: '15px',
                    display: 'flex',
                    gap: '12px',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    lineHeight: '1.5',
                    justifyContent: 'space-between'
                  }}>
                    <span style={{ 
                      fontWeight: '600',
                      color: isDark ? '#ffffff' : COLORS.THEME.LIGHT.TEXT_PRIMARY 
                    }}>
                      {event.fuelLevel.toFixed(2)}L
                    </span>
                    <span style={{ 
                      color: isTheft ? '#ef4444' : '#10b981',
                      fontWeight: '500'
                    }}>
                      {changeStr}
                    </span>
                    <span style={{ 
                      color: isTheft ? '#ef4444' : '#10b981',
                      fontWeight: '500'
                    }}>
                      {percentStr}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* Tooltip for timeline items */}
          {hoveredTimelineItem !== null && timelineItemTooltipPosition && sortedTimeline[hoveredTimelineItem] && (() => {
            const event = sortedTimeline[hoveredTimelineItem];
            const isTheft = event.type === 'THEFT';
            const timestamp = new Date(event.timestamp);
            const changeStr = `${isTheft ? '-' : '+'}${Math.abs(event.fuelChange).toFixed(2)}L`;
            const percentStr = `${event.percentChange > 0 ? '+' : ''}${event.percentChange.toFixed(2)}%`;
            
            return (
              <div
                style={{
                  position: 'fixed',
                  left: `${timelineItemTooltipPosition.x}px`,
                  top: `${timelineItemTooltipPosition.y - 10}px`,
                  transform: 'translate(-50%, -100%)',
                  backgroundColor: isDark ? '#1f2937' : '#ffffff',
                  border: `2px solid ${isTheft ? '#ef4444' : '#10b981'}`,
                  borderRadius: '8px',
                  padding: '14px 16px',
                  boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
                  pointerEvents: 'none',
                  zIndex: 1000,
                  minWidth: '220px',
                  marginBottom: '8px',
                }}
              >
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: '700',
                  color: isTheft ? '#ef4444' : '#10b981',
                  marginBottom: '8px'
                }}>
                  {isTheft ? '🔴 Fuel Theft Detected' : '🟢 Fuel Refill Recorded'}
                </div>
                <div style={{ 
                  fontSize: '15px', 
                  fontWeight: '600',
                  color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                  marginBottom: '6px'
                }}>
                  Fuel Level: {event.fuelLevel.toFixed(2)}L
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
                  marginBottom: '6px',
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '600' }}>Change:</span>
                  <span style={{ 
                    color: isTheft ? '#ef4444' : '#10b981',
                    fontWeight: '600'
                  }}>
                    {changeStr}
                  </span>
                  <span style={{ 
                    color: isTheft ? '#ef4444' : '#10b981',
                    fontWeight: '600'
                  }}>
                    ({percentStr})
                  </span>
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
                  borderTop: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                  paddingTop: '8px',
                  marginTop: '8px'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>Timestamp:</div>
                  {timestamp.toLocaleString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      {/* <div className="page-header">
        <h2>Fuel Management</h2>
        <p>Monitor fuel levels and track fuel theft/refill events</p>
      </div> */}

      <div className="card" style={{ height: 'calc(100vh - 185px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', marginTop: '24px' }}>
        <div className="card-header" style={{ flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
            <label style={{ 
              color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
              fontSize: '16px',
              fontWeight: '500'
            }}>
              Select Date:
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={getMaxDateToday()}
              className="search-input"
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '16px',
                width: '180px',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {/* Left: Ambulance List */}
          <div style={{ 
            width: '300px', 
            flexShrink: 0,
            backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND,
            border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
            borderRadius: '6px',
            padding: '16px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Fixed Header */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              marginBottom: '16px',
              flexShrink: 0,
            }}>
              <h3 style={{ 
                margin: 0,
                color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                fontSize: '18px',
                fontWeight: '600'
              }}>
                Ambulances
              </h3>
              <button
                onClick={() => {
                  setAmbulanceSearchOpen(!ambulanceSearchOpen);
                  if (ambulanceSearchOpen) {
                    setAmbulanceSearchTerm('');
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                title="Search ambulances"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </button>
            </div>
            
            {/* Fixed Search Input */}
            {ambulanceSearchOpen && (
              <div style={{ marginBottom: '12px', flexShrink: 0 }}>
                <input
                  type="text"
                  placeholder="Search by name or license plate..."
                  value={ambulanceSearchTerm}
                  onChange={(e) => setAmbulanceSearchTerm(e.target.value)}
                  className="search-input"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
                    backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND,
                    color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                    boxSizing: 'border-box',
                  }}
                  autoFocus
                />
              </div>
            )}

            {/* Scrollable List Container */}
            <div style={{ 
              flex: 1, 
              minHeight: 0, 
              overflowY: 'auto',
              overflowX: 'hidden',
            }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '20px', color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY }}>
                  Loading...
                </div>
              ) : ambulances.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY }}>
                  No ambulances found
                </div>
              ) : (() => {
                // Filter ambulances based on search term
                const filteredAmbulances = ambulanceSearchTerm
                  ? ambulances.filter((ambulance) => {
                      const name = (ambulance.metadata?.name || '').toLowerCase();
                      const licensePlate = (ambulance.spec?.licensePlate || '').toLowerCase();
                      const searchLower = ambulanceSearchTerm.toLowerCase();
                      return name.includes(searchLower) || licensePlate.includes(searchLower);
                    })
                  : ambulances;

                if (filteredAmbulances.length === 0 && ambulanceSearchTerm) {
                  return (
                    <div style={{ textAlign: 'center', padding: '20px', color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY }}>
                      No ambulances found matching &quot;{ambulanceSearchTerm}&quot;
                    </div>
                  );
                }

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {filteredAmbulances.map((ambulance) => {
                    const isSelected = selectedAmbulance?._id === ambulance._id;
                    const hasGpsDevice = !!ambulance.spec?.gpsDevice?.deviceId;
                    const ambulanceStatus = ambulance.spec?.status;
                    const statusBadgeStyle = getAmbulanceStatusBadgeStyle(ambulanceStatus, isDark);
                    const fuelReadingsKnown = ambulance._id in fuelReadingsAvailability;
                    const hasFuelReadings = fuelReadingsAvailability[ambulance._id] === true;
                    const cardBackground = getFuelCardBackground(hasFuelReadings, fuelReadingsKnown, isDark);
                    const cardHoverBackground = getFuelCardHoverBackground(hasFuelReadings, fuelReadingsKnown, isDark);
                    return (
                      <button
                        key={ambulance._id}
                        onClick={() => setSelectedAmbulance(ambulance)}
                        disabled={!hasGpsDevice}
                        style={{
                          padding: '12px',
                          borderRadius: '6px',
                          border: `2px solid ${isSelected ? '#3b82f6' : (isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER)}`,
                          backgroundColor: isSelected
                            ? (isDark ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)')
                            : cardBackground,
                          color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                          cursor: hasGpsDevice ? 'pointer' : 'not-allowed',
                          opacity: hasGpsDevice ? 1 : 0.5,
                          textAlign: 'left',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          if (hasGpsDevice && !isSelected) {
                            e.currentTarget.style.backgroundColor = cardHoverBackground;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (hasGpsDevice && !isSelected) {
                            e.currentTarget.style.backgroundColor = cardBackground;
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                          <div style={{ fontWeight: '600', minWidth: 0 }}>
                            {ambulance.metadata.name}
                          </div>
                          {ambulanceStatus && (
                            <span
                              style={{
                                flexShrink: 0,
                                fontSize: '12px',
                                fontWeight: 600,
                                padding: '2px 8px',
                                borderRadius: '999px',
                                lineHeight: 1.4,
                                ...statusBadgeStyle,
                              }}
                            >
                              {formatAmbulanceStatus(ambulanceStatus)}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '14px', color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY }}>
                          {ambulance.spec.licensePlate || 'No license plate'}
                          {!hasGpsDevice && ' • No GPS Device'}
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            </div>
          </div>

          {/* Right: Fuel Data */}
          <div style={{ 
            flex: 1, 
            minWidth: 0,
            backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND,
            border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
            borderRadius: '6px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}>
            {fuelLoading ? (
              <div style={{ 
                padding: '40px', 
                textAlign: 'center', 
                color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY 
              }}>
                Loading fuel data...
              </div>
            ) : !selectedAmbulance ? (
              <div style={{ 
                padding: '40px', 
                textAlign: 'center', 
                color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY 
              }}>
                Please select an ambulance
              </div>
            ) : !selectedAmbulance.spec?.gpsDevice?.deviceId ? (
              <div style={{ 
                padding: '40px', 
                textAlign: 'center', 
                color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY 
              }}>
                Selected ambulance does not have a GPS device
              </div>
            ) : (() => {
              const hasReadings = !!(fuelData && fuelData.fuelReadings && fuelData.fuelReadings.length > 0);
              const hasTimeline = !!(fuelData && fuelData.fuelTimeline && fuelData.fuelTimeline.length > 0);
              const hasDistance = !!(fuelData && fuelData.locationHistory && fuelData.locationHistory.length > 0);
              const hasSummary = !!fuelData?.summary;
              
              // If nothing at all (no readings, no timeline, no distance, no summary), show single message
              if (!hasReadings && !hasTimeline && !hasDistance && !hasSummary) {
                return (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    padding: '40px',
                    textAlign: 'center',
                    color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY 
                  }}>
                    No data available for selected date
                  </div>
                );
              }
              
              return (
                <div style={{ display: 'flex', gap: 0, height: '100%', minHeight: 0, overflow: 'hidden' }}>
                  {/* Left: Fuel Timeline - 30% */}
                  <div style={{ 
                    width: '30%', 
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    height: '100%',
                  }}>
                    {renderTimeline()}
                  </div>
                  
                  {/* Divider line with space on top and bottom */}
                  <div style={{
                    width: '1px',
                    backgroundColor: isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER,
                    marginTop: '20px',
                    marginBottom: '20px',
                    flexShrink: 0,
                  }} />
                  
                  {/* Right: Fuel Readings Chart - 70% */}
                  <div style={{ 
                    width: '70%', 
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    height: '100%',
                  }}>
                    {renderFuelChart()}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Fuel;

